<template>
  <b-modal
    v-if="opdracht"
    v-model="visible"
    :busy="busy"
    show
    size="xl"
    @ok="onOk"
    @hide="onHide"
  >
    <template #modal-title>
      <b-card-title>
        Liftmanagement traject {{ opdracht.naam }} voor
        {{ opdracht.relatie.naam }}
      </b-card-title>
      <b-card-sub-title>{{ trajectPath }}</b-card-sub-title>
    </template>

    <b-overlay 
      :show="busy" 
      variant="dark"
    >
      <b-row>
        <b-col cols="3">
          <b-form-row>
            <b-col>
              <b-form-group label="Naam">
                <b-input v-model="opdracht.naam" />
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col>
              <b-form-group label="Omschrijving">
                <b-textarea v-model="opdracht.omschrijving" />
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-group label="Status">
            <b-radio-group
              v-model="opdracht.status"
              :options="[
                'INSCHRIJVING',
                'GUNNING',
                'UITVOERING',
                'OPLEVERING',
                'AFGESLOTEN',
              ]"
              stacked
              buttons
              class="w-100"
            />
          </b-form-group>

          <b-form-group label="Opdrachtnummer">
            <b-input
              v-model="opdracht.opdrachtnummer"
              placeholder="Het opdrachtnummer van de klant"
            />
          </b-form-group>

          <b-form-group label="Opdracht bestand">
            <secure-upload
              v-if="opdracht.opdrachtbestand == null"
              :endpoint="authDocPath"
              subfolder="opdrachten"
              @file-uploaded="opdracht.opdrachtbestand = $event.document"
            />

            <b-button-toolbar v-if="opdracht.opdrachtbestand != null">
              <b-link
                :href="opdracht.opdrachtbestand.secure_url"
                target="_blank"
              >
                <font-awesome-icon icon="download" /> Download bestand
              </b-link>
            </b-button-toolbar>
          </b-form-group>
        </b-col>

        <b-col>
          <b-tabs v-model="selectedTab">
            <b-tab 
              title="Planning" 
              title-item-class="h5"
            >
              <b-form-checkbox 
                v-model="directVerwijderen" 
                :button-variant="directVerwijderen ? 'danger': 'ghost'"
                size="lg"
                class="mb-2 float-right"
                button 
              >
                <font-awesome-icon icon="trash" /> Direct stappen verwijderen
              </b-form-checkbox>
              <b-table
                :items="opdracht.events"
                :bordered="directVerwijderen"
                :hover="directVerwijderen"
                :small="!directVerwijderen"
                :fields="[
                  { key: 'status', sortable: true },
                  'data.omschrijving',
                  {
                    key: 'op',
                  },
                  {
                    key: 'data.unit.unitkenmerk',
                    label: 'Unit',
                    sortable: true,
                  },
                  { key: 'data.unit.adres', label: 'Adres', sortable: true },
                  'aan',
                  'actie',
                ]"
                primary-key="data.id"
                @row-clicked="(item) => directVerwijderen ? deleteTaak(item) : showDialogEdit(item)"
              >
                <template #cell(status)="row">
                  {{ row.item.data.planningstatus }} 
                </template>

                <template #cell(#)="row">
                  {{ row.index + 1 }}
                </template>

                <template #cell(aan)="row">
                  {{
                    row.item.data.toegewezen_aan != null
                      ? row.item.data.toegewezen_aan.Naam
                      : ""
                  }}
                  <font-awesome-icon
                    v-if="row.item.data.gecontroleerd_door"
                    icon="user-check"
                  />
                </template>

                <template #cell(op)="row">
                  <span v-if="row.item.data.datum == null">
                    <font-awesome-icon icon="stopwatch" />
                    {{ moment(row.item.data.deadline).fromNow() }}
                  </span>
                  <span v-else>
                    <font-awesome-icon icon="calendar" />
                    {{ moment(row.item.data.datum).format("L") }}
                  </span>
                </template>

                <template #cell(actie)="row">
                  <b-button
                    v-if="row.item.data.type == 'Facturatie'"
                    :disabled="opdracht.status == 'GEFACTUREERD'"
                    size="sm"
                    @click="
                      () => createInvoice({ amount: row.item.data.bedrag })
                    "
                  >
                    Maak conceptfactuur {{ row.item.data.bedrag | formatNumber("currency") }}
                    <font-awesome-icon 
                      icon="arrow-right"
                    />
                  </b-button>
                </template>
              </b-table>
              <traject-menu 
                :disabled="opdracht.status == 'GEFACTUREERD'" 
                @action="insertTaak($event)"
              />
            </b-tab>

            <b-tab
              title="Wizard uitvoering"
              title-item-class="h5"
            >
              <unit-select 
                v-model="selectedUnit"
                :relatie-id="opdracht.relatie.id"
                @unit-selected="(item) => selectedUnit = item"
              />

              <uitvoering-wizard 
                v-if="selectedUnit !== null"
                :traject-id="id"
                :unit="selectedUnit"
                @uitvoering-completed="onUitvoeringCompleted"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </b-overlay>

    <b-modal
      v-if="inputStapObject != null"
      v-model="inputStap"
      :title="inputStapObject.data.type"
      size="lg"
      @ok="() => updateTaak(inputStapObject)"
      @hide="inputStap = false"
    >
      <component
        :is="inputStapObject.data.type"
        v-if="inputStapObject != null"
        :totaal="Number(opdracht.bedrag)"
        :activiteit="inputStapObject.data"
        :authdocpath="authDocPath"
      />

      <template #modal-footer="{ ok }">
        <b-button
          id="popover-reactive-1"
          class="mr-auto"
          variant="danger"
          @click="deleteTaak(inputStapObject)"
        >
          Verwijderen
        </b-button>
        <b-button 
          variant="success" 
          @click="ok()"
        >
          Opslaan
        </b-button>
      </template>
    </b-modal>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        id="popover-reactive-1"
        :disabled="opdracht.events.length > 0"
        class="mr-auto"
        variant="danger"
        @click="() => deleteOpdracht(opdracht)"
      >
        Verwijderen
      </b-button>

      <b-button 
        variant="secondary" 
        @click="cancel()"
      >
        Annuleren
      </b-button>

      <b-button 
        variant="success" 
        @click="ok()"
      >
        Opslaan
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import { verkoopMixin } from "@/mixins/verkoopMixin";
import SecureUpload from "../documenten/SecureUpload.vue";
import FontAwesome from "../../icons/FontAwesome.vue";

import Facturatie from "../../../components/trajecten/FacturatieStap.vue";
import NulInspectie from "../../../components/trajecten/InspectieStap.vue";
import Uitvoering from "../../../components/trajecten/UitvoeringStap.vue";

import Standaard from "../../../components/trajecten/StandaardStap.vue";
import Algemeen from "../../../components/trajecten/StandaardStap.vue";
import Unit from "../../../components/trajecten/StandaardStap.vue";

import UitvoeringWizard from "../../../components/trajecten/UitvoeringWizard.vue";
import UnitSelect from '../../../components/UnitSelect.vue';
import TrajectMenu from '../../../components/trajecten/TrajectMenu.vue';

export default {
  name: "Opdracht",
  components: {
    SecureUpload,
    FontAwesome,
    Facturatie,
    NulInspectie,
    Standaard,
    Algemeen,
    Uitvoering,
    Unit,
    UitvoeringWizard,
    UnitSelect,
    TrajectMenu,
  },
  mixins: [verkoopMixin],
  props: {
    id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      visible: true,
      inputStap: false,
      inputStapObject: null,
      opdracht: null,
      selectedTab: null,
      selectedUnit: null,
      directVerwijderen: false
    };
  },
  computed: {
    trajectPath: function () {
      if (this.id != null) return `/management/${this.id}`;
    },
    authDocPath: function () {
      return `/documenten/donkenliftadvies/${new Date().getUTCFullYear()}/authenticated`;
    },
    takenPath: function () {
      return this.trajectPath + "/activiteiten";
    },
  },
  watch: {
    id: {
      immediate: true,
      handler: function (newval) {
        console.log("trigger id", newval);
        if (newval !== null) this.getTraject(newval);
      },
    },
  },
  methods: {
    onUitvoeringCompleted(result) {
      console.log(result);
      this.opdracht.events.push(...result.planning)
      this.selectedTab = 0
    },
    deleteTaak(taak) {
      var vm = this;
      console.log(taak);
      this.deleteTrajectTaak(this.takenPath, { payload: taak }).then(() => {
        vm.opdracht.events.splice(vm.opdracht.events.indexOf(taak), 1);
        vm.inputStap = false;
        vm.inputStapObject = null;
      });
    },
    insertTaak(type) {
      var vm = this;
      this.insertTrajectTaak(this.takenPath, {
        payload: { data: { type: type.value } },
      }).then(function (response) {
        console.log("ingevoerd");
        console.log(response.data);
        var taak = response.data.taak;
        taak.data.omschrijving = type.label;
        vm.opdracht.events.push(taak);
        vm.showDialogEdit(taak);
      });
    },
    updateTaak(taak) {
      // var vm = this;
      this.updateTrajectTaak(this.takenPath, { payload: taak }).then(function (
        response
      ) {
        console.log("geupdate");
        taak = response.data.taak;
        console.log(response.data);
      });
    },
    showDialogEdit(taak) {
      this.inputStap = true;
      this.inputStapObject = taak;
    },
    onOk(modal) {
      modal.preventDefault();
      this.update();
    },
    update() {
      console.log("update...");
      var vm = this;
      this.updateTraject(this.trajectPath, { traject: this.opdracht }).then(
        function (response) {
          console.log(response.data);
          vm.emitUpdate(response.data.traject);
        }
      );
    },
    onHide(modal) {
      console.log(modal.trigger);
      if (modal.trigger !== "ok") this.goBack();
    },
    deleteOpdracht(opdracht) {
      console.log(opdracht)
      var vm  = this;
      this.deleteTraject(this.trajectPath, {traject: opdracht }).then(
        function (response) {
          console.log(response.data);
          vm.emitDelete(opdracht);
        }
      );
    },
    getTraject() {
      var vm = this;
      this.get(this.trajectPath).then(function (response) {
        console.log(response);
        vm.opdracht = response.data.traject;
      });
    },
  },
};
</script>